import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api, downloadBlob } from '../api/client'
import type { CensoSbomProduct } from '../api/types'
import { usePermessiModuli } from '../auth/permessi'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'
import { Card, ErrorBanner, Spinner } from './common'
import { DataTable, type Column } from './DataTable'

/**
 * CENSO's own SBOM and VEX, for the products of this installation (the
 * platform, the agent, the probe): CENSO asks suppliers for them and gives
 * its own. The SBOM is downloaded with the SHA-256 of every file distributed;
 * the same documents are in the supplier registry under DiscoveryGuard Ltd.
 * See censo_sbom_service.
 */
export function SbomCenso({ prodotti, titolo }: { prodotti?: CensoSbomProduct['slug'][]; titolo?: string }) {
  const dati = useAsync(() => api.censoSbom(), [])
  const permessi = usePermessiModuli()
  const [errore, setErrore] = useState<unknown>(null)

  async function scarica(p: CensoSbomProduct, kind: 'sbom' | 'vex') {
    setErrore(null)
    try {
      const blob = await downloadBlob(api.censoSbomPath(p.slug, kind))
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `${p.name.toLowerCase().replace(/\s+/g, '-')}-${p.version || 'sbom'}${kind === 'vex' ? '.vex' : ''}.cdx.json`
      a.click()
      URL.revokeObjectURL(a.href)
    } catch (e) {
      setErrore(e)
    }
  }

  const righe = (dati.data?.products ?? []).filter((p) => !prodotti || prodotti.includes(p.slug))
  const fornitore = dati.data?.supplier_id
  const colonne: Column<CensoSbomProduct>[] = [
    { key: 'name', header: t('Prodotto'), value: (p) => p.name },
    { key: 'version', header: t('Versione'), value: (p) => p.version,
      render: (p) => (p.available ? <code>{p.version.slice(0, 12)}</code> : <span className="text-muted">—</span>) },
    { key: 'components', header: t('Componenti'), type: 'number', value: (p) => p.components,
      render: (p) => (p.available ? p.components.toLocaleString(localeCorrente()) : t('non presente in questa installazione')) },
    { key: 'vex', header: 'VEX', value: (p) => p.statements,
      render: (p) => (!p.has_vex ? <span className="text-muted">{t('nessuna dichiarazione')}</span> : (
        <span>
          {t('{n} dichiarazioni, {na} «non interessato»', { n: p.statements, na: p.not_affected })}
          {p.exploitable > 0 && <> · <span className="badge badge--error">{t('{n} sfruttabili', { n: p.exploitable })}</span></>}
        </span>
      )) },
    { key: 'generated_at', header: t('Generata'), type: 'date', value: (p) => p.generated_at ?? '',
      render: (p) => (p.generated_at ? new Date(p.generated_at).toLocaleDateString(localeCorrente()) : '—') },
    { key: 'azioni', header: '', sort: false, searchable: false, render: (p) => (
      <span className="estensione-azioni">
        <button type="button" className="secondary" disabled={!p.available} onClick={() => void scarica(p, 'sbom')}>
          {t('Scarica SBOM')}
        </button>
        <button type="button" className="secondary" disabled={!p.has_vex} onClick={() => void scarica(p, 'vex')}>
          {t('Scarica VEX')}
        </button>
      </span>
    ) },
  ]

  return (
    <Card title={titolo ?? t('SBOM e VEX di CENSO')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('L’elenco dei componenti (CycloneDX) della versione installata qui, letto dai file distribuiti, con l’impronta SHA-256 di ciascun file; la VEX dice, per ogni vulnerabilità nota dei componenti, se il prodotto ne è interessato e perché. Produttore: DiscoveryGuard Ltd.')}
        {fornitore && permessi.vede('suppliers') && (
          <> <Link to={`/suppliers/${fornitore}?tab=documenti`}>{t('Gli stessi documenti nel registro fornitori')}</Link></>
        )}
      </p>
      <ErrorBanner error={errore ?? dati.error} />
      {dati.loading && !dati.data ? <Spinner /> : (
        <DataTable rows={righe} columns={colonne} rowKey={(p) => p.slug} strumenti={false} paginazione={false}
                   emptyText={t('Nessun documento.')} />
      )}
    </Card>
  )
}
